import { ColumnDef } from '@tanstack/react-table';
import { SourcingOpenings } from 'api-clients/monolith';
import _isEmpty from 'lodash/isEmpty';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { PerformanceStatusPill } from '@/components/base/performance-status-pill';
import { SourcingStatusPill } from '@/components/base/sourcing-status-pill';
import { Button } from '@/components/ui/button';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { Checkbox } from '@/components/ui/checkbox';
import { ConversionStats } from '@/components/ui/conversion-stats';
import { CurrencyStats } from '@/components/ui/currency-stats';
import { Duration } from '@/components/ui/duration';
import { HireStats } from '@/components/ui/hire-stats';
import { OpeningStatus } from '@/components/ui/opening-status';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useNoHiringGoalDialog } from '@/contexts/no-hiring-goal-dialog-context';
import { useHireUrl } from '@/hooks/use-hire-url';
import { formatShortDate } from '@/utils/date-util';

import { FlashAlert } from './flash-alert';
import { Menu } from './menu';
import { useAutomationFlashMessages } from './use-automation-flash-alerts';
import { useEditTargets } from './use-edit-targets';

export function useDashboardColumns(): ColumnDef<SourcingOpenings>[] {
  const { handleEditTargets } = useEditTargets();
  const { openNoHiringGoalDialog } = useNoHiringGoalDialog();
  const navigate = useNavigate();
  const {
    setOpenChannelsDialog,
    setSelectedOpeningIds,
    setIneligibleOpenings,
  } = useJobPostingContext();

  const { getAutomationFlashMessages } = useAutomationFlashMessages();

  const handleNoHiringGoal = (rowItem: SourcingOpenings) => {
    return openNoHiringGoalDialog({
      title: rowItem.title,
      brandSlug: rowItem.brand_slug,
      locationId: rowItem.location_id,
      funnelId: rowItem.external_id,
    });
  };

  const handleTitleClick = (rowItem: SourcingOpenings) => {
    if (!rowItem.hiring_goal) return handleNoHiringGoal(rowItem);

    return navigate(`${rowItem.slug}/channels`);
  };

  const handleTargetsClick = (rowItem: SourcingOpenings) => {
    if (!rowItem.hiring_goal) return handleNoHiringGoal(rowItem);

    return handleEditTargets(rowItem);
  };

  const handlePostJobClick = (opening: SourcingOpenings) => {
    const isEligible = opening.active && opening.is_sourcing_funnel;
    setSelectedOpeningIds(isEligible ? [opening.external_id] : []);
    setIneligibleOpenings(
      isEligible
        ? []
        : [
            {
              externalId: opening.external_id,
              title: opening.title,
              reason: !opening.active ? 'inactive' : 'sourcing_disabled',
              brandSlug: opening.brand_slug ?? 'no_brand',
              locationId: opening.location_id,
            },
          ],
    );
    setOpenChannelsDialog(true);
  };

  const { getHireATHiringGoalUrl } = useHireUrl();

  return [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected()
              ? true
              : table.getIsSomePageRowsSelected()
              ? 'indeterminate'
              : false
          }
          onCheckedChange={value => table.toggleAllPageRowsSelected(!!value)}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={value => row.toggleSelected(!!value)}
        />
      ),
    },
    {
      accessorKey: 'title',
      header: () => (
        <FormattedMessage
          defaultMessage="Opening name"
          description="Dashboard table: job title column with link to opening"
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex min-w-[200px] max-w-[280px] items-center gap-1 overflow-hidden">
            <OpeningStatus active={row.original.active} />
            <Button
              variant="link"
              onClick={() => handleTitleClick(row.original)}
              className="whitespace-break-spaces h-auto text-left"
            >
              {row.original.title}
            </Button>
          </div>
        );
      },
    },
    {
      accessorKey: 'performance_status_metrics',
      header: () => (
        <FormattedMessage
          defaultMessage="Status"
          description="Dashboard row: on-track vs at-risk vs goal met for the hire period"
        />
      ),
      cell: ({ row }) => {
        const performanceStatusMetrics =
          row.original.performance_status_metrics;
        const { status, criteria } = performanceStatusMetrics;

        if (!status) return <span>-</span>;

        return (
          <PerformanceStatusPill
            status={status}
            criteria={criteria}
            expected={
              criteria === 'hire'
                ? performanceStatusMetrics.expected_hires_count
                : performanceStatusMetrics.expected_applicants_count
            }
            actual={
              criteria === 'hire'
                ? row.original.hire_stats.actual
                : row.original.applicants_stats.actual
            }
            startDate={
              row.original.hiring_goal?.start_date &&
              formatShortDate(row.original.hiring_goal?.start_date)
            }
            endDate={
              row.original.hiring_goal?.hire_by &&
              formatShortDate(row.original.hiring_goal?.hire_by)
            }
            duration={row.original.hiring_goal?.duration ?? undefined}
            manageHiringGoalLink={getHireATHiringGoalUrl(
              row.original.brand_slug ?? 'no_brand',
              row.original.location_id,
              row.original.external_id,
            )}
          />
        );
      },
    },
    {
      accessorKey: 'hire_stats',
      header: () => (
        <FormattedMessage
          defaultMessage="Hires"
          description="Column: hires vs target for the opening’s goal"
        />
      ),
      cell: ({ row }) => (
        <HireStats
          stats={row.original.hire_stats}
          isHighlighted={row.original.has_cross_funnel_transitions}
          openingSlug={row.original.slug}
        />
      ),
    },
    {
      accessorKey: 'total_duration',
      header: () => (
        <FormattedMessage
          defaultMessage="Duration"
          description="Column: time left in the hire-by window"
        />
      ),
      cell: ({ row }) => {
        const automationMessageObj = row.original.flash_alert_messages.find(
          msg => msg.automation_type === 'hiring_goal_ended',
        );
        const automationMessage = getAutomationFlashMessages(
          automationMessageObj,
          row.original.duration_stats,
        );

        const status = row.original.performance_status_metrics?.status ?? null;
        const showDurationWarning =
          row.original.hiring_goal_percentage >= 90 && status !== 'goal_met';

        return (
          <div className="flex items-center gap-1">
            <Duration
              stats={row.original.duration_stats}
              brand={row.original.brand_slug ?? 'no_brand'}
              location={row.original.location_id}
              funnel={row.original.external_id}
              showNewGoalButton={!row.original.hiring_goal?.is_current}
              hiringGoalAutomationEnabled={
                !!automationMessageObj?.automation_type
              }
              showDurationWarning={showDurationWarning}
            />
            {automationMessage && (
              <FlashAlert description={automationMessage.description} />
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'conversion_stats',
      header: () => (
        <FormattedMessage
          defaultMessage="Conversion"
          description="Column: actual vs expected applicant-to-hire rate"
        />
      ),
      cell: ({ row }) => (
        <ConversionStats
          stats={row.original.conversion_stats}
          onClick={() => handleTargetsClick(row.original)}
        />
      ),
    },
    {
      accessorKey: 'applicants_stats',
      header: () => (
        <FormattedMessage
          defaultMessage="Applicants"
          description="Column: pipeline volume vs target applicants"
        />
      ),
      cell: ({ row }) => {
        const automationMessageObj = row.original.flash_alert_messages.find(
          msg => msg.automation_type === 'automated_as_goal_met',
        );
        const automationMessage =
          getAutomationFlashMessages(automationMessageObj);

        return (
          <div className="flex items-center gap-1">
            <HireStats
              stats={row.original.applicants_stats}
              openingSlug={row.original.slug}
            />
            {automationMessage && (
              <FlashAlert description={automationMessage.description} />
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'budget_stats',
      header: () => (
        <FormattedMessage
          defaultMessage="Budget spend"
          description="Column: dollars used vs target budget for the goal"
        />
      ),
      cell: ({ row }) => {
        const automationMessageObj = row.original.flash_alert_messages.find(
          msg => msg.automation_type === 'target_budget_met',
        );
        const automationMessage =
          getAutomationFlashMessages(automationMessageObj);

        return (
          <div className="flex items-center gap-1">
            <CurrencyStats
              stats={row.original.budget_stats}
              onClick={
                row.original.hiring_goal?.is_current
                  ? () => handleTargetsClick(row.original)
                  : undefined
              }
              isHighlighted={row.original.has_cross_funnel_spend}
              openingSlug={row.original.slug}
            />
            {automationMessage && (
              <FlashAlert description={automationMessage.description} />
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'total_cpa',
      header: () => (
        <FormattedMessage
          defaultMessage="CPA"
          description="Column: cost per applicant (acronym kept short for layout)"
        />
      ),
      cell: ({ row }) => (
        <CurrencyStats
          stats={row.original.cpa_stats}
          onClick={
            row.original.hiring_goal?.is_current
              ? () => handleTargetsClick(row.original)
              : undefined
          }
        />
      ),
    },
    {
      accessorKey: 'total_cph',
      header: () => (
        <FormattedMessage
          defaultMessage="CPH"
          description="Column: cost per hire (acronym kept short for layout)"
        />
      ),
      cell: ({ row }) => (
        <CurrencyStats
          stats={row.original.cph_stats}
          onClick={
            row.original.hiring_goal?.is_current
              ? () => handleTargetsClick(row.original)
              : undefined
          }
        />
      ),
    },
    {
      accessorKey: 'channels',
      header: () => (
        <FormattedMessage
          defaultMessage="Channels"
          description="Column: which paid boards this opening is on (icons only)"
        />
      ),
      cell: ({ row }) => {
        const { channels } = row.original;

        if (_isEmpty(channels)) return <span>-</span>;

        return (
          <div className="flex w-max">
            {channels?.map(channel => (
              <ChannelIcon
                key={channel}
                channelSlug={channel}
                className="-mr-1 last:mr-0"
              />
            ))}
          </div>
        );
      },
    },
    {
      accessorKey: 'sourcing_status',
      header: () => (
        <FormattedMessage
          defaultMessage="Sourcing"
          description="Column: org-wide sourcing status (on/off, automation, etc.)"
        />
      ),
      cell: ({ row }) => {
        const sourcingStatus = row.original.sourcing_status;

        if (!sourcingStatus) return <span>-</span>;

        return <SourcingStatusPill status={sourcingStatus} />;
      },
    },
    {
      accessorKey: 'brand_name',
      header: () => (
        <FormattedMessage
          defaultMessage="Brand"
          description="Column: employer brand for the requisition"
        />
      ),
      cell: ({ row }) => (
        <Typography variant="subtitle1">{row.original.brand_name}</Typography>
      ),
    },
    {
      accessorKey: 'menu',
      header: () => null,
      cell: ({ row }) => {
        return (
          <Menu
            opening={row.original}
            onEditTargets={() => handleTargetsClick(row.original)}
            onPostJob={() => handlePostJobClick(row.original)}
          />
        );
      },
    },
  ];
}
